/**
 * Dashboard SPA — shared types, constants, and shell-level helpers.
 *
 * Tab type + TAB_ORDER drive the per-tab RouteDef factory in `index.ts`.
 * tabLabel/navHtml/shellHtml render the chrome (header, tab nav, PWA
 * banner, panel container) common to every tab.
 *
 * PWA banner gates: `shouldShowPwaBanner()` is sync (called during render),
 * checks localStorage dismiss flag + standalone state + visit counter so
 * we don't nudge first-time users.
 *
 * Originally lines 36-148 of `routes/dashboard.ts`. Phase 2.2 split 2026-05-12.
 */
import { Auth } from '../../auth';
import { icon } from '../../utils/icons';
import { escapeHtml } from '../../utils';
import { t } from '../../i18n';

export type Tab = 'esims' | 'analytics' | 'history' | 'referral' | 'support' | 'settings';

export const TAB_ORDER: Tab[] = ['esims', 'analytics', 'history', 'referral', 'support', 'settings'];

export function tabLabel(tab: Tab): string {
  switch (tab) {
    case 'esims': return t('dash.tabs.esims');
    case 'analytics': return t('dash.tabs.analytics');
    case 'history': return t('dash.tabs.history');
    case 'referral': return t('dash.tabs.referral');
    case 'support': return t('dash.tabs.support');
    case 'settings': return t('dash.tabs.settings');
  }
}

function navHtml(active: Tab): string {
  return `
<nav class="dash-tabs" aria-label="Dashboard sections">
  ${TAB_ORDER.map(
    (tab) => `<a class="dash-tab ${tab === active ? 'is-active' : ''}" href="/dashboard/${tab}">${escapeHtml(tabLabel(tab))}</a>`,
  ).join('')}
</nav>`;
}

/* PWA + push banner: dismiss persists across locale switches, page
 * reloads, tab refreshes — anywhere localStorage is alive on the same
 * origin. Earlier impl rendered the banner with a `hidden` attribute
 * and let JS un-hide it on mount; operator reported the dismiss didn't
 * survive locale change (2026-05-09). The new flow is render-time
 * gated: we check localStorage + standalone state synchronously in
 * shellHtml() and emit no markup at all if any "skip" condition fires.
 *
 * 2026-05-09 v2 — banner now nudges TWO things instead of one:
 *   1. Add Roamzy to home screen (PWA install)
 *   2. Enable push notifications (low-balance + payment alerts)
 * The "Enable alerts" CTA is in-place: it triggers subscribePush() on
 * click without leaving the dashboard. Hidden once both states are
 * satisfied OR user dismissed. */
export const PWA_BANNER_DISMISS_KEY = 'roamzy.pwa-banner-dismissed.v1';

export function isAppStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
  return false;
}

function isPushAlreadyGranted(): boolean {
  // Synchronous best-effort check. We can't await a real PushSubscription
  // read here (this runs in render), but `Notification.permission` plus
  // the user's previous subscribe action is enough to skip the nudge.
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return true; // not supported → nothing to nudge for
  return Notification.permission === 'granted';
}

function shouldShowPwaBanner(): boolean {
  try {
    if (localStorage.getItem(PWA_BANNER_DISMISS_KEY) === '1') return false;
  } catch { /* private-mode browsers throw on localStorage access */ }
  // Hide only when BOTH actions are done — installed AND notifications on.
  if (isAppStandalone() && isPushAlreadyGranted()) return false;
  // Engagement gate (operator request 2026-05-09): don't nudge first-time
  // users. Show after the Nth dashboard visit; counter bumped in mount().
  try {
    const visits = parseInt(localStorage.getItem('roamzy.dashboard-visits') ?? '0', 10);
    if (Number.isFinite(visits) && visits < 3) return false;
  } catch { /* private-mode → conservatively show */ }
  return true;
}

export function shellHtml(active: Tab, body: string): string {
  const user = Auth.getUser();
  const firstName = user?.name ? user.name.split(' ')[0] : '';
  const showPwaBanner = shouldShowPwaBanner();
  return `
<section class="dash-route">
  <div class="container">
    <header class="dash-header">
      <div>
        <div class="section__kicker" style="margin-bottom:8px;" data-i18n="dash.kicker">${escapeHtml(t('dash.kicker'))}</div>
        <h1 class="dash-title">${escapeHtml(t('dash.greeting'))}${firstName ? `, ${escapeHtml(firstName)}` : ''}.</h1>
      </div>
      <div class="dash-header__cta">
        <button class="btn btn--accent" data-action="open-topup" data-i18n="dash.topupCta"${user?.suspended ? ' disabled' : ''}>${escapeHtml(t('dash.topupCta'))}</button>
      </div>
    </header>
    ${user?.suspended ? `<p class="dash-notice" role="status" data-i18n="dash.esims.suspendedNotice">${escapeHtml(t('dash.esims.suspendedNotice'))}</p>` : ''}
    ${navHtml(active)}
    ${
      showPwaBanner
        ? `<aside class="dash-pwa-banner" data-pwa-banner>
      <span class="dash-pwa-banner__icon" aria-hidden="true">${icon('bell', 18)}</span>
      <div class="dash-pwa-banner__body">
        <span class="dash-pwa-banner__text" data-i18n="dash.pwaBanner.text">${escapeHtml(t('dash.pwaBanner.text'))}</span>
        <div class="dash-pwa-banner__actions">
          <a href="/guides#pwa" class="dash-pwa-banner__link" data-pwa-banner-pin>${escapeHtml(t('dash.pwaBanner.linkPin'))} →</a>
          <button type="button" class="dash-pwa-banner__link dash-pwa-banner__link--btn" data-pwa-banner-enable hidden>${escapeHtml(t('dash.pwaBanner.linkEnable'))} →</button>
        </div>
      </div>
      <button type="button" class="dash-pwa-banner__close" data-pwa-banner-close aria-label="Dismiss">✕</button>
    </aside>`
        : ''
    }
    <main class="dash-panel" data-tab="${active}">
      <div class="dash-panel-inner">${body}</div>
    </main>
  </div>
</section>`;
}
